export type BackupId = number;
export type RestoreTaskId = number;
export type RestoreDatabaseId = number;
export type RestoreWebsiteId = number;
export type RestoreServerId = number;

export enum ScheduleType {
  Backup = 'backup-schedule',
  Prune = 'prune-schedule',
  Forget = 'forget-schedule',
}

export enum TasksTypes {
  Backup = 'backup-tasks',
  Restore = 'restore-tasks',
  Forget = 'forget-tasks',
  Prune = 'prune-tasks',
}

export enum BackupType {
  FILE = 'file',
  MYSQL = 'mysql',
  FULL = 'full',
}

export enum BackupActionMode {
  Backup = 'backup',
  Prune = 'prune',
  Forget = 'forget',
}

export enum TaskProgressStatus {
  Pending = 0,
  Running = 1,
  Finished = 2,
  Failed = 3,
}

export enum RestoreTaskType {
  ServerAndConfig = 0,
  ServerOnly = 1,
  ConfigOnly = 2,
}

export enum BackupRestoreType {
  WEBSITE = 'website',
  MYSQL = 'mysql',
}

export enum BackupScope {
  ACCOUNT = 'account',
  SERVER = 'server',
  FULL = 'full',
}

export enum BackupEndStatus {
  PENDING = 'pending',
  SUCCESS = 'success',
  FAILED = 'failed',
  PARTIAL = 'partial',
  SCHEDULED = 'scheduled',
}

export interface CreateInstantBackupDTO {
  repositoryId: BackupRepoId;
}

export interface RestoreBackupOptionsDTO {
  restoreServer: boolean;
  restoreConfigs: boolean;
}

interface WebsiteRestoreBackupDTO {
  type: BackupRestoreType.WEBSITE;
  targetBackupId: number;
  options?: RestoreBackupOptionsDTO;
}

interface MysqlRestoreBackupDTO {
  type: BackupRestoreType.MYSQL;
  targetBackupId: number;
  options?: RestoreBackupOptionsDTO;
}

export type RestoreBackupDTO = WebsiteRestoreBackupDTO | MysqlRestoreBackupDTO;

export interface BackupSummaryDTO {
  id: BackupId;
  type: BackupType;
  scope: BackupScope;
  status: BackupEndStatus;
  createdAt: Date;
}

export interface RestoreDatabase {
  id: RestoreDatabaseId;
  status: TaskProgressStatus;
}

export interface RestoreWebsite {
  id: RestoreWebsiteId;
  status: TaskProgressStatus;
}

export interface RestoreServer {
  id: RestoreServerId;
  status: TaskProgressStatus;
}

export interface RestoreTaskSummaryDTO {
  id: RestoreTaskId;
  type: RestoreTaskType;
  status: TaskProgressStatus;
  startDate: Date;
  endDate: Date;
  backup: BackupSummaryDTO;
  websiteRestores: WebsiteRestore[];
}

export interface WebsiteRestore {
  id: number;
  isMain: boolean;
  restoreTaskId: number;
  status: WebsiteRestoreStatus;
  websiteDomain: string;
  websiteGroupId: number;
  websiteId: number;
  restoreConfigsJson: string;
  websiteRestoreTargets: WebsiteRestoreBackupTarget[];
}

export enum WebsiteRestoreStatus {
  Pending = 0,
  RunningConfig = 1,
  PendingTargets = 2,
  RunningTargets = 3,
  Finished = 4,
  Failed = 5,
}

export interface WebsiteRestoreBackupTarget {
  id: number;
  status: TaskProgressStatus;
  restoreWebsiteId: number;
  restoreBackupTargetId: number;
  restoreBackupTarget?: WebsiteBackupTarget;
}

export interface DatabaseBackupSummaryDTO {
  id: DatabaseBackupId;
  websiteId: number;
  databaseId: number;
  databaseName: string;
}

export type DatabaseBackupId = number;
export type DatabaseBackupConfigDTO = unknown;

export interface WebsiteBackupSummaryDTO {
  id: WebsiteBackupId;
  websiteId: number;
}

export type WebsiteBackupId = number;
export type WebsiteBackupConfigDTO = unknown;

export interface BackupDTO {
  id: BackupId;
  type: BackupType;
  scope: BackupScope;
  status: BackupEndStatus;
  snapshotId: string;
  repo: BackupRepoDTO;
  createdAt: Date;
}

export type BackupScheduleId = number;

export interface CreateBackupScheduleDTO {
  repositoryId: BackupRepoId;
  intervalDays: number;
  runAtHour: number;
  runAtMinute: number;
  retries: number;
}

export interface CreateInstantForgetRepositoryDTO {
  backupServerId: BackupServerId;
  repositoryIds: BackupRepoId[];
  retentionDays: number;
}

export interface CreateInstantPruneRepositoryDTO {
  backupServerId: BackupServerId;
  repositoryIds: BackupRepoId[];
}

export interface CreateSchedulePruneRepositoryDTO {
  backupServerId: BackupServerId;
  repositoryIds: BackupRepoId[];
  intervalDays: number;
  runAtHour: number;
  runAtMinute: number;
  retries: number;
}

export interface CreateScheduledForgetRepositoryDTO {
  backupServerId: BackupServerId;
  repositoryIds: BackupRepoId[];
  intervalDays: number;
  runAtHour: number;
  runAtMinute: number;
  retries: number;
  retentionDays: number;
}

export enum BackupTaskStatus {
  PENDING = 0,
  RUNNING = 1,
  COMPLETED = 2,
  FAILED = 3,
  PARTIAL = 4,
}

export type BackupTaskId = number;

export interface QueryResultDTO<TData> {
  data: TData[];
  total: number;
}

export interface BackupTaskSummaryDTO {
  id: BackupTaskId;
  scheduleId: BackupScheduleId;
  repo: BackupRepoSummaryDTO;
  status: BackupTaskStatus;
  runDate: Date;
  retriesLeft: number;
  createdAt: Date;
}

export interface BackupTaskDTO {
  id: BackupTaskId;
  schedule: BackupScheduleDTO;
  status: BackupTaskStatus;
  runDate: Date;
  retriesLeft: number;
  startDate: Date | null;
  endDate: Date | null;
  error: string | null;
  createdAt: Date;
  backups: BackupDTO[];
}

export interface BackupScheduleDTO {
  id: BackupScheduleId;
  repositoryId: BackupRepoId;
  intervalDays: number;
  runAtHour: number;
  runAtMinute: number;
  lastRunDay: Date;
  retries: number;
  createdAt: Date;
  repository: BackupRepoDTO;
}

export type BackupRepoId = number;

export interface BackupRepoDTO {
  id: BackupRepoId;
  repositoryId: string;
  repositoryPath: string;
  repositoryPassword: string;
  passwordPath: string;
  originServerId: string;
  originServerHostname: string;
  backupServer: BackupServerDTO;
}

export interface BackupRepoSummaryDTO {
  id: BackupRepoId;
  originServerId: string;
  originServerHostname: string;
}

export interface CreateBackupServerDTO {
  name: string;
}

export type BackupServerId = number;
export interface BackupServerDTO {
  id: BackupServerId;
  name: string;
}

export interface PagingOptions {
  page: number;
  pageSize: number;
  serverId?: string;
  websiteId?: number;
  timestampFrom?: number;
  timestampTo?: number;
}

export interface SearchOptions {
  search?: string;
}

export type PagingWithSearchOptions = PagingOptions & SearchOptions;

export type BackupLogId = number;

export enum LogType {
  INFO = 'info',
  ERROR = 'error',
  WARNING = 'warning',
}

export interface BackupLogDTO {
  id: BackupLogId;
  backupId: BackupId;
  date: Date;
  type: LogType;
  log: string;
}

export enum TargetType {
  WEBSITE = 0,
  DATABASE = 1,
}

export type FailedConfigCollectionLogId = number;
export interface FailedConfigCollectionLogDTO {
  id: FailedConfigCollectionLogId;
  date: Date;
  targetType: TargetType;
  websiteId: number;
  message: string;
}

export enum BackupCreationType {
  Instant = 'instant',
  Scheduled = 'scheduled',
}

export enum ConfigResetActionName {
  RESET_DATABASE = 'reset_database',
  RESET_DNS = 'reset_dns',
  RESET_FTP = 'reset_ftp',
  RESET_MAIL = 'reset_mail',
  RESET_SSL = 'reset_ssl',
  RESET_SYSTEM_USER = 'reset_system_user',
  RESET_VHOST = 'reset_vhost',
}

export enum ConfigResetStatus {
  PENDING = 'pending',
  DONE = 'done',
  FAILED = 'failed',
}

export enum ConfigResetActionStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in progress',
  FAILED = 'failed',
  DONE = 'done',
  CANCELLED = 'cancelled',
}

export type ConfigResetId = number;
export type ConfigResetActionId = number;

export interface ConfigResetActionDTO<TConfig = unknown> {
  id: ConfigResetActionId;
  action: ConfigResetActionName;
  parentActionIds: ConfigResetActionId[] | null;
  status: ConfigResetActionStatus;
  config: TConfig;
  configResetId: ConfigResetId;
  logs: ConfigResetActionLogDTO[];
}

export interface ConfigResetActionLogDTO {
  id: number;
  actionId: ConfigResetActionId;
  log: string;
  createdAt: Date;
}

export interface ConfigResetSummaryDTO {
  id: ConfigResetId;
  websiteId: number;
  status: ConfigResetStatus;
  createdAt: Date;
  type: BackupRestoreType;
  isMain: boolean;
  groupId: number;
  restoreTaskId: number;
  backupTargetId: number;
}

export interface ConfigResetDTO {
  id: ConfigResetId;
  websiteId: number;
  status: ConfigResetStatus;
  createdAt: Date;
  actions: ConfigResetActionDTO[];
}

export interface ConfigResetListDTO {
  websiteRestores: ConfigResetDTO[];
}

export interface CreateForgetScheduleDTO {
  backupServerId: BackupServerId;
  intervalDays: number;
  runAtHour: number;
  runAtMinute: number;
  retentionDays: number;
  retries: number;
}

export enum LockReason {
  LOCK_REASON_UNSPECIFIED = 0,
  LOCK_REASON_FORGET = 1,
  LOCK_REASON_PRUNE = 2,
  LOCK_REASON_BACKUP = 3,
  LOCK_REASON_RESTORE = 4,
  LOCK_REASON_MANUAL = 5,
  UNRECOGNIZED = -1,
}

export enum LockType {
  LOCK_TYPE_UNSPECIFIED = 0,
  LOCK_TYPE_LOCK_ALL = 1,
  LOCK_TYPE_ALLOW_RESTORE = 2,
  UNRECOGNIZED = -1,
}

export type RepoLockId = number;
export interface RepoLocksDTO {
  id: number;
  repoId: RepoLockId;
  reason: LockReason;
  type: LockType;
  active: boolean;
  createdAt: Date;
}

// New models

export interface WebsiteBackupTarget {
  id: number;
  snapshotTarget: string;
  snapshotTargetType: string;
  type: string;
  uniqueIdentifier: string;
  websiteBackupId: number;
}

export interface WebsiteBackup {
  backupId: number;
  backup: Backup;
  configJson: string;
  domain: string;
  id: number;
  isMain: boolean;
  status: BackupEndStatus;
  websiteBackupTargets: WebsiteBackupTarget[];
  websiteGroupId: number;
  websiteId: number;
}

export interface Backup {
  backupTaskId: number;
  createdAt: string;
  id: number;
  mainWebsiteDomain?: string;
  repo: BackupRepository;
  repositoryId: number;
  scope: string;
  status: string;
  systemUserName?: string;
  targetOriginServer: BackupOriginServer;
  targetOriginServerId: number;
  task: BackupTask;
  websiteGroupId?: number;
}

export interface BackupRepository {
  backupServerId: number;
  id: number;
  repositoryId: string;
  repositoryPasswordPath: string;
  repositoryPath: string;
  retentionDays: number;
}

export interface BackupOriginServer {
  defaultRepoId: number;
  id: number;
  originServerHostname: string;
  originServerId: string;
  terminatedRepoId: number;
}

export interface BackupTask {
  createdAt: string;
  endDate?: string;
  id: number;
  mainWebsiteDomain?: string;
  operationContext?: string;
  retriesLeft: number;
  runDate: string;
  scheduleId: number;
  scope: string;
  startDate?: string;
  status: 4;
  systemUserName?: string;
  targetOriginServerId: number;
  websiteGroupId?: number;
}

export interface WebsiteRestoreDto {
  websiteBackupId: number;
  restoreConfigTypes?: string[];
  restoreBackupTargets?: number[];
}

export interface RequestRestoreDto {
  backupId: number;
  websiteRestoreList: WebsiteRestoreDto[];
}
